import { SkillTypeDto } from '@/api/generated';

import { componentsTranslations } from './comnponents/components.translations.en.ts';
import { type AppTranslations, NAMESPACE } from './translations.pl.ts';
import { utilsTranslations } from './util/utils.translations.en.ts';
import { viewsTranslations } from './views/views.translations.en.ts';

export const enTranslation: AppTranslations = {
  [NAMESPACE]: {
    components: componentsTranslations,
    // LEGAL: draft English translation of the GDPR consents and the data processing information.
    // It must be approved by the Orange Polska legal team before English goes to production.
    consents: {
      additionalPersonalDataProcessingConsentOrange:
        'I consent to the processing of the data I have voluntarily provided by Orange Polska S.A. with its registered office in Warsaw. I agree that Orange Polska S.A. (with its registered office in Warsaw) may process personal data that is not required by law but that I have voluntarily provided in my application for the purposes of the recruitment process I have indicated for Orange Polska S.A.',
      collapse: 'collapse',
      dataProcessingInfoHtml: `<p>By submitting your application, you consent to the processing of the personal data you have provided in this recruitment process, including sending you a request for an anonymous, voluntary opinion on the quality of the recruitment.</p><br/><p>The controller of your personal data is Orange Polska S.A. with its registered office in Warsaw, at Aleje Jerozolimskie 160, 02-326 Warszawa (hereinafter Orange Polska). Contact details of the data protection officer at Orange Polska: inspektorochronydanych@orange.com</p><br/><p>In the recruitment process we will process the data required by law and other data obtained during recruitment meetings, including from tests you took part in and other methods of verifying competences and skills used during recruitment meetings, obtained with your consent. This data will be used to establish your competences and skills and to assess them against the requirements of the position you are applying for.</p><br/><p>Your data will be processed for the purpose of conducting the recruitment process on the basis of the law (legal obligation of the data controller), and for data whose processing does not result from the law and for the purpose of sending you a request for an anonymous, voluntary opinion on the quality of the recruitment - on the basis of your consent:</p><br/><p>&ndash; for a period of 12 months from the date of submitting the application,</p><br/><p>&ndash; for a period of 24 months from the date of submitting the application, if you have consented to the processing of your data in future recruitments.</p><br/><p>Providing data including your first name and last name, date of birth, correspondence address, email address or phone number, education, professional qualifications and employment history results from the law. Providing other data is voluntary.</p><br/><p>Your personal data may be transferred to:</p><br/><p>a) processors that cooperate with Orange Polska in conducting recruitment or that provide legal assistance,</p><br/><p>b) entities that operate ICT systems on our behalf or provide us with ICT tools, and their subcontractors.</p><br/><p>You have the right to:</p><br/><p>- withdraw your consent at any time; withdrawing consent does not affect the lawfulness of processing based on consent before its withdrawal,</p><br/><p>- request access to your data processed by Orange Polska,</p><br/><p>- rectify (correct) your data,</p><br/><p>- erase your data or restrict its processing,</p><br/><p>- data portability, which means the right to receive the data processed by Orange Polska or to request its transfer to another entity,</p><br/><p>- object to the processing of your data.</p><br/><p>The scope of each of these rights and the situations in which they can be exercised result from the law. Which right you can exercise will depend, for example, on the legal basis and purpose of the data processing. To exercise the above rights, you can send a request by post to: Orange Polska S.A., Al. Jerozolimskie 160, 02-326 Warszawa or by email to rekrutacja@orange.com. If</p><br/><p>necessary, we may ask you to provide additional information to authenticate you in order to check whether you are entitled to submit the request.</p><br/><p>If you believe that your personal data is processed unlawfully, you can lodge a complaint with the President of the Personal Data Protection Office (UODO).</p><br/><p>In addition, by submitting your application you confirm that you have read the Joint internal procedure for reporting breaches of law and taking follow-up actions in the entities of the Orange Polska Capital Group, hereinafter referred to as the &quot;Internal Reporting Procedure&rdquo;.</p><br/><p>The Internal Reporting Procedure is available at:</p><br/><p>https://www.orange.pl/etyka-i-compliance</p>
`,
      dataProcessingInfoTitle:
        'Information on the processing of personal data at Orange Polska S.A. with its registered office in Warsaw',
      expand: 'expand',
      futureRecruitmentDataProcessingConsentOrange:
        'I consent to the processing of my personal data contained in the documents I have provided by Orange Polska S.A. with its registered office in Warsaw for the purpose of participating in future recruitment processes conducted at Orange Polska S.A. ',
      requiredConsent: 'required consent',
      warningNoConsent: "If you don't tick the consent above, we won't be able to contact you",
    },
    quizFinishedPage: {
      messages: {
        '0': {
          message: "That's almost everything",
          subMessage:
            'Your application is almost ready. The business areas you are interested in are: <span class="text-primary font-bold">{{ areas }}</span>',
        },
        '1': {
          message: "It's not over yet",
          subMessage:
            "In a moment I'll also ask you to rate your level of knowledge of the competences that are key for us in the areas you have chosen.",
        },
      },
    },
    quizInitializedPage: {
      buttons: {
        next: 'Next',
      },
      errorMessage: 'Failed to load the quiz question.',
      heading: 'Welcome to the Quiz App',
      unsupportedType: 'Unsupported question type',
    },
    skillsAssessmentCollectedPage: {
      buttons: {
        finish: 'Finish!',
      },
      heading: 'Thank you for this adventure together',
      subMessage1: 'Your application has just been sent. ',
      subMessage2:
        "You'll receive feedback from us within 3 business days. You can find a full description of the recruitment process ",
      subMessage3: 'Confirmation of receipt of your application',
      subMessage4: 'has been sent to you by email. ',
      subMessage5: "If the message hasn't arrived, contact us: {{ contactEmail }} ",
      subMessage6: 'See you!',
      here: 'here',
      competence: 'Competence',
      skillTypes: {
        [SkillTypeDto.ProgrammingLanguage]: 'Programming languages',
        [SkillTypeDto.Tool]: 'Tools',
        [SkillTypeDto.SystemTechnologyMethodology]: 'Systems, technologies, work methodologies',
        [SkillTypeDto.Language]: 'Languages',
      },
      introText:
        'Choose the competences you consider your strengths. Based on them, we will be able to match the right internship to your abilities and potential.',
      languageIntroText: 'Finally, rate your level of language skills:',
      standardRatingLabels: {
        noKnowledge: 'No knowledge',
        basic: 'Basic',
        good: 'Good',
        advanced: 'Advanced',
      },
      standardRatingDescriptions: {
        noKnowledge: 'I have no knowledge in this area',
        basic: 'I have basic, often theoretical knowledge and need support with practical tasks',
        good: 'I have the knowledge and can use it effectively in standard tasks, but need support with more complex ones',
        advanced:
          'I can use the technology efficiently and independently in most situations, even difficult ones',
      },
      standardRatingShortLabels: {
        noKnowledge: 'None',
        basic: 'Basic',
        good: 'Good',
        advanced: 'Adv.',
      },
      languageRatingLabels: {
        a1a2: 'A1/A2',
        b1b2: 'B1/B2',
        c1c2: 'C1/C2',
        native: 'Native',
      },
    },
    startGamePage: {
      buttons: {
        startGame: 'Start the game',
      },
      heading: 'What you should know before starting the recruitment game:',
      list: {
        listItem1:
          'you have {{ timeToCompleteInMinutes }} minutes to complete the game, which lets you dive into the world of Orange and solve the tasks at your own pace',
        listItem2: 'the tasks in the game have a similar level of difficulty',
        listItem3: 'find a quiet place where nothing will distract you',
        listItem4: 'make sure you have a good internet connection',
        listItem5:
          'the game has a soundtrack, so if you want to listen to it, get your headphones ready',
        listItem6: "after completing the game you'll receive feedback",
        listItem7: 'you can play the game only once',
        listItem8:
          'if you interrupt the game for technical reasons (e.g. no internet), you can come back to it, but only once and to the point where it was interrupted',
      },
    },
    utils: utilsTranslations,
    views: viewsTranslations,
    welcomeAgainPage: {
      buttons: {
        continue: 'Continue',
      },
      heading: 'Great to see you here again.',
      subMessage:
        "I was starting to think I'd lost touch with you. Let's not waste any time. You can continue the recruitment game.",
    },
    welcomePage: {
      buttons: {
        submit: "Let's go!",
      },
      // LEGAL: draft English translation of the email consent, see the note at consents above
      checkboxText:
        "I consent to the processing of my personal data (the email address provided) for the purpose of receiving email communication dedicated to recruitment for the Let's Orange internships.",
      description:
        'Gain experience and grow at Orange working on projects that matter. Time to play it your way and discover your own talents.',
      emailPlaceholder: 'Enter your email address',
      passwordPlaceholder: 'Password',
      emailPrompt: 'First, verify your email.',
      error: {
        wentWrong: 'Something went wrong',
      },
      heading1: 'The future',
      heading2: 'is here',
      popup: {
        message: "We've sent a verification link to the email address you provided",
        subMessage:
          'Click the link in the message you received and go on to the next steps of the recruitment.',
        emailNotReceived1: "Email didn't arrive? Check your spam folder or click ",
        emailNotReceived2: ' to resend it.',
        emailNotReceivedHere: 'here',
        resendEmailSent: ' Done!',
      },
      subheading1: "Apply for Let's Orange internships without a CV.",
      subheading2: 'Enter the recruitment game and play it your way.',
    },
  },
};
